import { useCallback, useEffect, useState } from "react";
import { ProfitabilityCalculator } from "../components/ProfitabilityCalculator";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";

interface FinancialSummary {
  cashCollected: number;
  closerPayouts: number;
  setterPayouts: number;
  takeHome: number;
  totalExpenses: number;
  netProfit: number;
  callsBooked?: number;
  showRate?: number;
  closeRate?: number;
  avgDealSize?: number;
}

export function RevProjector() {
  const { formatForApi } = useDateRange();
  const { offer } = useFilters();
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      const dates = formatForApi();
      const params = new URLSearchParams({
        startDate: dates.startDate,
        endDate: dates.endDate,
      });

      if (offer && offer !== "all") {
        params.set("offer", offer);
      }

      const res = await fetch(`/api/financials/summary?${params.toString()}`);
      const data = await res.json();

      if (data.success) {
        setSummary({
          cashCollected: data.summary.cashCollected || 0,
          closerPayouts: data.summary.closerPayouts || 0,
          setterPayouts: data.summary.setterPayouts || 0,
          takeHome: data.summary.takeHome || 0,
          totalExpenses: data.summary.totalExpenses || 0,
          netProfit: data.summary.netProfit || 0,
          callsBooked: data.summary.callsBooked || 0,
          showRate: data.summary.showRate || 0,
          closeRate: data.summary.closeRate || 0,
          avgDealSize: data.summary.avgDealSize || 0,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [formatForApi, offer]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return (
    <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">Rev Projector</h1>
        <p className="text-sm text-muted-foreground">
          Scenario modeling based on the current date range and selected offer.
        </p>
      </div>

      {isLoading && !summary ? (
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="h-[720px] animate-pulse rounded-xl bg-muted/40" />
        </div>
      ) : (
        <ProfitabilityCalculator actuals={summary} />
      )}
    </div>
  );
}
